import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, ShoppingCart } from "lucide-react";
import { byId, CATALOG, QUOTE_ONLY, TAX_READY, type CatalogItem } from "../data/catalog";
import { clearCart, setQuantity, useCart } from "../lib/cart";

// The checkout Edge Function (rareworks-leads supabase/functions/checkout).
// It prices the order itself from its own copy of the catalog, so nothing
// sent from this page can change what is charged.
const CHECKOUT_URL = "https://kdwixjyqhrkjlzkoojfa.supabase.co/functions/v1/checkout";

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const per = (i: CatalogItem) => (i.billing === "monthly" ? "/mo" : i.billing === "yearly" ? "/yr" : "");
// Taxable items cannot be sold online until the sales tax permit is in hand.
const canBuy = (i: CatalogItem) => TAX_READY || !i.taxable;

const CATEGORIES = [...new Set(CATALOG.map((i) => i.category))];
const slug = (category: string) => category.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const field =
  "w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700 text-white placeholder-gray-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-colors outline-hidden";

const Cart = () => {
  const cart = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "" });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [rep, setRep] = useState("");

  // ?r=<rep id> credits the rep who sent the link; remembered for this browser.
  // ?paid=1 is where Stripe sends people back after paying.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    try {
      const r = q.get("r") ?? window.localStorage.getItem("rw-rep") ?? "";
      if (/^[0-9a-f-]{36}$/i.test(r)) {
        window.localStorage.setItem("rw-rep", r);
        setRep(r);
      }
    } catch {
      /* storage off: no rep credit */
    }
    if (q.get("paid") === "1") {
      clearCart();
      setSent(true);
    }
  }, []);

  const lines = useMemo(
    () => Object.entries(cart).filter(([id]) => byId[id]).map(([id, quantity]) => ({ item: byId[id], quantity })),
    [cart],
  );
  // The phone checkout bar is fixed to the bottom: leave room so it never hides the end of the footer.
  const barShowing = !sent && lines.length > 0;
  useEffect(() => {
    document.body.classList.toggle("max-lg:pb-16", barShowing);
    return () => document.body.classList.remove("max-lg:pb-16");
  }, [barShowing]);
  const buying = lines.filter((l) => canBuy(l.item));
  const asking = lines.filter((l) => !canBuy(l.item));
  const sum = (billing: string) =>
    buying.filter((l) => l.item.billing === billing).reduce((n, l) => n + l.item.cents * l.quantity, 0);
  const monthly = sum("monthly");
  const dueToday = sum("once") + sum("yearly") + buying.filter((l) => l.item.billing === "monthly" && !l.item.freeMonth).reduce((n, l) => n + l.item.cents * l.quantity, 0);
  const itemCount = lines.reduce((n, l) => n + l.quantity, 0);
  const questions = [...new Set(lines.flatMap((l) => l.item.ask ?? []))].filter((q) => q !== "Business name");
  const mixed = new Set(buying.filter((l) => l.item.billing !== "once").map((l) => l.item.billing)).size > 1;
  const missing = lines
    .filter((l) => l.item.needs && !l.item.needs.some((id) => cart[id]))
    .map((l) => `${l.item.name} needs a monthly plan with it. Add one under Care plans.`)
    .concat(mixed ? ["Yearly items and monthly plans are paid for separately. Finish one, then come back for the other."] : []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!lines.length) return setError("Your cart is empty.");
    if (missing.length) return setError(missing[0]);
    if (!agree) return setError("Please agree to the Terms to continue.");
    setBusy(true);
    try {
      const res = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: lines.map((l) => ({ id: l.item.id, quantity: l.quantity })),
          customer: form,
          answers: questions.map((q) => ({ q, a: answers[q] ?? "" })).filter((x) => x.a.trim()),
          rep,
          agreed: true,
        }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || "Something went wrong. Please try again, or call us.");
      window.gtag?.("event", "begin_checkout", { value: dueToday / 100, currency: "USD" });
      if (out.url) {
        window.location.href = out.url; // Stripe's payment page
        return;
      }
      clearCart(); // request only: nothing to pay online
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative min-h-screen pt-32 pb-28">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-semibold text-white mb-4">
            Build your order
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl">
            Every price here is what every customer pays. Monthly plans start with 30 days free, and you can cancel any
            time, except a managed business site, which has a 12-month minimum.
          </p>
        </div>

        {sent ? (
          <div className="p-8 rounded-2xl glass border border-teal-500/40 max-w-2xl">
            <h2 className="text-2xl font-semibold text-white mb-3">Thank you. We have your order.</h2>
            <p className="text-gray-300 leading-relaxed">
              Alan or Kayla will be in touch within one business day to get started. If you paid, Stripe has emailed
              your receipt. You can sign in to your account at{" "}
              <a href="https://leads.rare-works.com/account/" className="text-teal-400 underline underline-offset-2">
                leads.rare-works.com/account
              </a>{" "}
              with the email you used here.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-10 items-start">
            <div>
              <nav aria-label="Jump to a category" className="flex flex-wrap gap-2 mb-10">
                {CATEGORIES.map((category) => (
                  <a
                    key={category}
                    href={`#${slug(category)}`}
                    className="px-3 py-1.5 rounded-lg border border-white/10 text-sm text-gray-400 hover:text-white hover:border-teal-500/50 transition-colors"
                  >
                    {category}
                  </a>
                ))}
              </nav>
              {CATEGORIES.map((category) => (
                <div key={category} id={slug(category)} className="mb-10 scroll-mt-28">
                  <h2 className="text-xl font-semibold text-white mb-4">{category}</h2>
                  <div className="grid gap-3">
                    {CATALOG.filter((i) => i.category === category).map((i) => {
                      const quantity = cart[i.id] ?? 0;
                      return (
                        <div
                          key={i.id}
                          className={`p-5 rounded-2xl glass border sm:flex sm:items-center sm:justify-between sm:gap-6 ${quantity ? "border-teal-500/50" : "border-gray-700/50"}`}
                        >
                          <div>
                            <h3 className="text-white font-semibold">{i.name}</h3>
                            <p className="text-gray-400 text-sm leading-relaxed mt-1">{i.desc}</p>
                            {i.specs && (
                              <ul className="mt-2 space-y-0.5 text-xs text-gray-500 list-disc pl-4">
                                {i.specs.map((s) => (
                                  <li key={s}>{s}</li>
                                ))}
                              </ul>
                            )}
                            {!canBuy(i) && (
                              <p className="text-gray-400 text-xs mt-2">
                                Not sold online yet. Add it and we will contact you to set it up.
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-3 sm:mt-0 shrink-0">
                            <p className="text-white font-semibold whitespace-nowrap">
                              {money(i.cents)}
                              <span className="text-gray-400 font-normal text-sm">{per(i)}</span>
                            </p>
                            {quantity && i.quantity ? (
                              <div className="flex items-center gap-2">
                                <button type="button" aria-label={`One fewer ${i.name}`} onClick={() => setQuantity(i.id, quantity - 1)} className="w-9 h-9 rounded-lg border border-gray-600 text-white flex items-center justify-center hover:border-teal-400">
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-6 text-center text-white font-semibold">{quantity}</span>
                                <button type="button" aria-label={`One more ${i.name}`} onClick={() => setQuantity(i.id, quantity + 1)} className="w-9 h-9 rounded-lg border border-gray-600 text-white flex items-center justify-center hover:border-teal-400">
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setQuantity(i.id, quantity ? 0 : 1)}
                                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${quantity ? "border border-gray-600 text-gray-300 hover:border-rose-400 hover:text-white" : "bg-linear-to-r/srgb from-teal-500 to-cyan-600 text-white"}`}
                              >
                                {quantity ? "Remove" : canBuy(i) ? "Add" : "Request"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <h2 className="text-xl font-semibold text-white mb-4">Quoted, not sold here</h2>
              <div className="grid gap-3">
                {QUOTE_ONLY.map((q) => (
                  <a key={q.name} href={q.href} className="block p-5 rounded-2xl glass hover:border-teal-500/50 transition-colors">
                    <h3 className="text-white font-semibold">{q.name}</h3>
                    <p className="text-gray-400 text-sm mt-1">{q.desc}</p>
                  </a>
                ))}
              </div>
            </div>

            <form id="order" onSubmit={submit} className="scroll-mt-28 lg:sticky lg:top-28 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto p-6 rounded-2xl glass">
              <h2 className="flex items-center gap-2 text-xl font-semibold text-white mb-4">
                <ShoppingCart className="w-5 h-5 text-teal-400" /> Your order
              </h2>
              {!lines.length && <p className="text-gray-400 text-sm">Nothing yet. Add something from the list.</p>}
              {buying.map((l) => (
                <div key={l.item.id} className="flex justify-between gap-4 text-sm py-1.5">
                  <span className="text-gray-300">{l.quantity > 1 ? `${l.quantity} x ` : ""}{l.item.name}</span>
                  <span className="text-white whitespace-nowrap">{money(l.item.cents * l.quantity)}{per(l.item)}</span>
                </div>
              ))}
              {asking.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700/60">
                  <p className="text-xs text-gray-400 mb-1">We will contact you about these (nothing is charged for them now):</p>
                  {asking.map((l) => (
                    <div key={l.item.id} className="flex justify-between gap-4 text-sm py-1">
                      <span className="text-gray-400">{l.quantity > 1 ? `${l.quantity} x ` : ""}{l.item.name}</span>
                      <span className="text-gray-400 whitespace-nowrap">{money(l.item.cents * l.quantity)}{per(l.item)}</span>
                    </div>
                  ))}
                </div>
              )}
              {buying.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700/60 text-sm">
                  <div className="flex justify-between py-1"><span className="text-gray-300">Due today</span><span className="text-white font-semibold">{money(dueToday)}</span></div>
                  {monthly > 0 && (
                    <div className="flex justify-between py-1"><span className="text-gray-300">Then each month, after 30 free days</span><span className="text-white font-semibold">{money(monthly)}</span></div>
                  )}
                </div>
              )}

              {lines.length > 0 && (
                <div className="mt-5 space-y-3">
                  <input className={field} required placeholder="Your name" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Your name" />
                  <input className={field} required type="email" placeholder="Email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-label="Email" />
                  <input className={field} required type="tel" placeholder="Phone" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} aria-label="Phone" />
                  <input className={field} required placeholder="Business name" autoComplete="organization" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} aria-label="Business name" />
                  {questions.map((q) => (
                    <label key={q} className="block text-sm text-gray-300">
                      {q}
                      <textarea className={`${field} mt-1 min-h-[70px]`} value={answers[q] ?? ""} onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })} />
                    </label>
                  ))}
                  <label className="flex items-start gap-3 text-sm text-gray-300">
                    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 w-4 h-4 accent-teal-500" />
                    <span>
                      I agree to the{" "}
                      <a href="/terms.html" target="_blank" rel="noopener" className="text-teal-400 underline underline-offset-2">Terms</a>{" "}
                      and{" "}
                      <a href="/privacy.html" target="_blank" rel="noopener" className="text-teal-400 underline underline-offset-2">Privacy Policy</a>. All sales are final, except a one-time order we have not started, which can be cancelled for a full refund.
                    </span>
                  </label>
                  {(error || missing.length > 0) && <p role="alert" className="text-rose-400 text-sm">{error || missing[0]}</p>}
                  <button type="submit" disabled={busy} className="w-full py-3.5 rounded-xl font-semibold text-white bg-linear-to-r/srgb from-teal-500 to-cyan-600 hover:brightness-110 transition-[filter] disabled:opacity-60">
                    {busy ? "One moment..." : buying.length ? "Continue to payment" : "Send my request"}
                  </button>
                  {buying.length > 0 && <p className="text-xs text-gray-500">You pay on Stripe's secure page. We never see your card number.</p>}
                </div>
              )}
            </form>
          </div>
        )}
      </div>
      {/* On a phone the order form is far below the list, so what's in the cart stays in view. */}
      {barShowing && (
        <a
          href="#order"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex items-center justify-between gap-4 px-5 py-4 border-t border-white/10 bg-gray-900 text-white"
        >
          <span className="text-sm">
            {itemCount} {itemCount === 1 ? "item" : "items"}
            {buying.length > 0 && <span className="text-gray-400"> · {money(dueToday)} due today</span>}
          </span>
          <span className="text-sm font-medium text-teal-400">Go to checkout</span>
        </a>
      )}
    </section>
  );
};

export default Cart;
